import React, { Component, type ComponentType } from 'react';
// eslint-disable-next-line jira/restricted/react-redux
import { Provider } from 'react-redux';
// eslint-disable-next-line jira/restricted/prop-types
import { object } from 'prop-types';
import {
	type FlagsMapper,
	type Store,
	type Action,
	type DismissFlag,
	type ShowFlagFn,
	DISMISS_COMMAND,
	type Flag,
	type FlagComponent as FlagComponentType,
} from '../../../services/types';

export type Props<TState, TAction extends Action> = {
	mapper: FlagsMapper<TAction, TState>;
	showFlag: ShowFlagFn;
	// eslint-disable-next-line jira/react/handler-naming
	dismissFlag: (arg1: DismissFlag) => void;
};
type Context<TState> = {
	store: Store<TState>;
};
/**
 * Facilitates state and action management across React components by integrating with a Redux store. Ensures compatibility with
 * the store's action listening capabilities and manages the lifecycle of action listeners.
 */
export const createDispatcher = <TAction extends Action, TState>(
	storeKey: string,
): ComponentType<Props<TState, TAction>> =>
	// eslint-disable-next-line jira/react/no-class-components
	class Dispatcher extends Component<Props<TState, TAction>> {
		static contextTypes = {
			[storeKey]: object.isRequired,
		};

		constructor(props: Props<TState, TAction>, context: Context<TState>) {
			super(props);

			// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'Context<TState, TAction>'.
			const store = context[storeKey];

			if (!store) {
				throw new Error(`Cannot get store with the key '${storeKey}' from the context.`);
			}
			if (
				typeof store.addActionListener !== 'function' ||
				typeof store.removeActionListener !== 'function'
			) {
				throw new Error(
					"Your store doesn't support action listeners; please use the Tangerine store initialiser.",
				);
			}
		}

		componentDidMount() {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			this.context[storeKey].addActionListener(this.perform);
		}

		componentWillUnmount() {
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			this.context[storeKey].removeActionListener(this.perform);
		}

		handleAction(action: TAction, state: TState) {
			const { mapper, showFlag, dismissFlag } = this.props;
			const result: Flag | DismissFlag | undefined = mapper(action, state);
			if (!result) {
				return;
			}

			// @ts-expect-error - TS2339 - Property 'command' does not exist on type 'DismissFlag | Flag'.
			if (result.command === DISMISS_COMMAND) {
				// @ts-expect-error - TS2322 - Type 'DismissFlag | Flag' is not assignable to type 'DismissFlag'.
				const flag: DismissFlag = result;
				if (flag.id) {
					dismissFlag(flag);
				}
				// @ts-expect-error - TS2339 - Property 'type' does not exist on type 'DismissFlag | Flag'. | TS2339 - Property 'render' does not exist on type 'DismissFlag | Flag'.
			} else if (result.type != null || result.render != null) {
				// @ts-expect-error - TS2345 - Argument of type 'DismissFlag | Flag' is not assignable to parameter of type 'Flag'.
				showFlag(result);
			} else {
				// @ts-expect-error - TS2322 - Type 'DismissFlag | Flag' is not assignable to type 'FlagComponent'.
				const FlagComponent: FlagComponentType = result;
				// go/jfe-eslint
				// eslint-disable-next-line react/no-unstable-nested-components
				const FlagComponentWithProvider = (props: {
					id: string | number;
					onDismissed?: (id: number | string) => void;
				}) => (
					// @ts-expect-error - TS2571 - Object is of type 'unknown'.
					<Provider store={this.context[storeKey]}>
						{/* @ts-expect-error - TS2322 - Type '{ id: string | number; onDismissed?: ((id: string | number) => void) | undefined; }' is not assignable to type '{ id: string | number; onDismissed: (id: string | number) => void; }'. */}
						<FlagComponent {...props} />
					</Provider>
				);
				showFlag(FlagComponentWithProvider);
			}
		}

		perform = (action: TAction, state: TState) => {
			// handle batch middleware
			if (action.meta && action.meta.batch && Array.isArray(action.payload)) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				action.payload.forEach((childAction: any) => {
					if (childAction.type && typeof childAction.type === 'string') {
						this.handleAction(childAction, state);
					}
				});
			} else {
				this.handleAction(action, state);
			}
		};

		render() {
			return null;
		}
	};
